import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import img from "./all_bg.jpg";

const BACKEND_URL = "https://api.bbsfacultyprofile.com";
const TABLE_NAME = "academic_experience";

const AcademicExperience = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [recordId, setRecordId] = useState(null);
  const [showCustomRank, setShowCustomRank] = useState(false);

  const [formData, setFormData] = useState({
    starting_month_year: null,
    ending_month_year: null,
    academic_rank: "",
    institution: "",
    nature_of_experience: "Full-time",
    country: "",
  });

  const fetchRecords = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/${TABLE_NAME}`, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch records");
      const data = await res.json();

const processed = data
  .map((r) => ({
    ...r,
    starting_month_year: r.starting_month_year
      ? new Date(r.starting_month_year)
      : null,
    ending_month_year: r.ending_month_year
      ? new Date(r.ending_month_year)
      : null,
  }))
  .sort((a, b) => {
    if (!a.starting_month_year) return 1;
    if (!b.starting_month_year) return -1;

    return (
      b.starting_month_year.getTime() -
      a.starting_month_year.getTime()
    );
  });

setRecords(processed);
    } catch (err) {
      console.error(err);
      toast.error(`Fetch error: ${err.message}`);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRankChange = (e) => {
    const value = e.target.value;
    if (value === "Other") {
      setShowCustomRank(true);
      setFormData({ ...formData, academic_rank: "" });
    } else {
      setShowCustomRank(false);
      setFormData({ ...formData, academic_rank: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        starting_month_year: formData.starting_month_year
          ? `${formData.starting_month_year.getFullYear()}-${String(
              formData.starting_month_year.getMonth() + 1
            ).padStart(2, "0")}-01`
          : null,

        ending_month_year: formData.ending_month_year
          ? `${formData.ending_month_year.getFullYear()}-${String(
              formData.ending_month_year.getMonth() + 1
            ).padStart(2, "0")}-01`
          : null,
      };
      const url = recordId
        ? `${BACKEND_URL}/api/${TABLE_NAME}/${recordId}`
        : `${BACKEND_URL}/api/${TABLE_NAME}`;
      const method = recordId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to save record");

      toast.success("Record saved successfully!");
      setFormData({
        starting_month_year: null,
        ending_month_year: null,
        academic_rank: "",
        institution: "",
        nature_of_experience: "Full-time",
        country: "",
      });
      setRecordId(null);
      setShowCustomRank(false);
      fetchRecords();
    } catch (err) {
      console.error(err);
      toast.error(`Submit error: ${err.message}`);
    }
  };

  const handleEdit = (record) => {
    setFormData({
      starting_month_year: record.starting_month_year,
      ending_month_year: record.ending_month_year,
      academic_rank: record.academic_rank,
      institution: record.institution,
      nature_of_experience: record.nature_of_experience || "Full-time",
      country: record.country,
    });
    setShowCustomRank(record.academic_rank === "Other");
    setRecordId(record.id);
    toast.info("Editing record...");
  };

  const handleDelete = (record) => {
    const toastId = toast.info(
      <div>
        Delete this record?{" "}
        <button
          onClick={async () => {
            try {
              const res = await fetch(
                `${BACKEND_URL}/api/${TABLE_NAME}/${record.id}`,
                { method: "DELETE", credentials: "include" }
              );
              if (!res.ok) throw new Error("Failed to delete record");
              fetchRecords();
              toast.update(toastId, {
                render: "Record deleted successfully!",
                type: "success",
                autoClose: 3000,
                closeButton: true,
              });
            } catch (err) {
              toast.update(toastId, {
                render: `Delete error: ${err.message}`,
                type: "error",
                autoClose: 3000,
                closeButton: true,
              });
            }
          }}
          style={{
            marginLeft: "10px",
            padding: "3px 8px",
            background: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Confirm
        </button>
      </div>,
      { autoClose: false, closeOnClick: false }
    );
  };

  const formatMonthYear = (date) => {
    if (!date) return "";
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${month}/${year}`;
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        fontFamily: "Arial, sans-serif",
        backgroundImage: `url(${img})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          background: "#fff",
          padding: "30px 40px",
          borderRadius: "10px",
          boxShadow: "0px 0px 12px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            color: "#1e3a8a",
            marginBottom: "25px",
          }}
        >
          Academic Experience
        </h2>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column" }}
        >
          {/* Dates Row */}
          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: "bold" }}>
                Starting Date(mm/yyyy)
              </label>
              <DatePicker
                selected={formData.starting_month_year}
                onChange={(date) =>
                  setFormData({ ...formData, starting_month_year: date })
                }
                dateFormat="MM/yyyy"
                showMonthYearPicker
                placeholderText="Select month/year"
                style={{ width: "100%" }}
              />
            </div>

            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: "bold" }}>
                Ending Date (mm/yyyy)
              </label>
              <DatePicker
                selected={formData.ending_month_year}
                onChange={(date) =>
                  setFormData({ ...formData, ending_month_year: date })
                }
                dateFormat="MM/yyyy"
                showMonthYearPicker
                placeholderText="Select month/year"
                style={{ width: "100%" }}
              />
            </div>
          </div>

          <label style={{ fontWeight: "bold", marginTop: "10px" }}>
            Academic Rank
          </label>
          <select
            name="academic_rank"
            value={showCustomRank ? "Other" : formData.academic_rank}
            onChange={handleRankChange}
            style={{
              padding: "10px",
              marginTop: "5px",
              border: "1px solid #ccc",
              borderRadius: "6px",
              width: "100%",
            }}
          >
            <option value="">Select</option>
            <option value="Professor"> Professor</option>
            <option value="Associate Professor">Associate Professor</option>
            <option value="Assistant Professor">Assistant Professor</option>
            <option value="Senior Lecturer">Senior Lecturer</option>
            <option value="Lecturer">Lecturer</option>
            <option value="Other">Other</option>
          </select>

          {showCustomRank && (
            <input
              type="text"
              placeholder="Enter academic rank"
              value={formData.academic_rank}
              onChange={(e) =>
                setFormData({ ...formData, academic_rank: e.target.value })
              }
              style={{
                padding: "10px",
                marginTop: "5px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                width: "100%",
              }}
              required
            />
          )}

          <label style={{ fontWeight: "bold", marginTop: "10px" }}>
            Institution
          </label>
          <input
            type="text"
            name="institution"
            value={formData.institution}
            onChange={handleChange}
            style={{
              padding: "10px",
              marginTop: "5px",
              border: "1px solid #ccc",
              borderRadius: "6px",
              width: "100%",
            }}
          />

          <label style={{ fontWeight: "bold", marginTop: "10px" }}>
            Nature of Experience
          </label>
          <select
            name="nature_of_experience"
            value={formData.nature_of_experience}
            onChange={handleChange}
            style={{
              padding: "10px",
              marginTop: "5px",
              border: "1px solid #ccc",
              borderRadius: "6px",
              width: "100%",
            }}
          >
            <option value="Full-time">Full-time</option>
            <option value="Part-time">Part-time</option>
          </select>

          <label style={{ fontWeight: "bold", marginTop: "10px" }}>
            Country
          </label>
          <input
            type="text"
            name="country"
            value={formData.country}
            onChange={handleChange}
            style={{
              padding: "10px",
              marginTop: "5px",
              border: "1px solid #ccc",
              borderRadius: "6px",
              width: "100%",
            }}
          />

          <button
            type="submit"
            style={{
              background: "#3b82f6",
              color: "white",
              padding: "12px",
              border: "none",
              borderRadius: "6px",
              marginTop: "20px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {recordId ? "Update Record" : "Add Record"}
          </button>
        </form>

        {records.length > 0 && (
          <div style={{ marginTop: "30px" }}>
            <h3 style={{ color: "#1e3a8a" }}>
              Your Academic Experience Records
            </h3>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr>
                  {[
                    "Start",
                    "End",
                    "Academic Rank",
                    "Institution",
                    "Nature",
                    "Country",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{ border: "1px solid #ccc", padding: "8px" }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <tr key={rec.id}>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {formatMonthYear(rec.starting_month_year)}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {formatMonthYear(rec.ending_month_year)}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.academic_rank}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.institution}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.nature_of_experience}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.country}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        textAlign: "center",
                      }}
                    >
                      <button
                        onClick={() => handleEdit(rec)}
                        style={{
                          background: "#2563eb",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          marginRight: "5px",
                          cursor: "pointer",
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(rec)}
                        style={{
                          background: "#ef4444",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "25px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "#1e3a8a",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcademicExperience;
